import { useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { MASTER_IMAGE, MASTER_ASPECT, REGIONS } from "../data/regions";

const MARQUEE_ITEMS =
  "A Nation · A Thousand Experiences · Culture · Heritage · Nature · People · Together · ";

export default function MasterAtlas({ onOpenRegion, onAwaiting }) {
  const [active, setActive] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const px = useSpring(useTransform(mx, [-0.5, 0.5], [10, -10]), { stiffness: 45, damping: 22 });
  const py = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 45, damping: 22 });

  const handleMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const activeRegion = REGIONS.find((r) => r.id === active);

  return (
    <div
      data-testid="master-atlas"
      className="fixed inset-0 archival-vignette grain flex flex-col overflow-hidden"
      onMouseMove={handleMouse}
    >
      {/* artwork stage */}
      <div className="flex-1 flex items-center justify-center pt-16 pb-10 px-4 sm:px-10">
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
          className="manuscript-frame relative"
          style={{
            width: `min(94vw, calc((100dvh - 170px) * ${MASTER_ASPECT}))`,
            aspectRatio: `${MASTER_ASPECT}`,
          }}
        >
          <span className="frame-corner tl" />
          <span className="frame-corner tr" />
          <span className="frame-corner bl" />
          <span className="frame-corner br" />

          <motion.div
            className="absolute inset-0"
            style={zoomed ? {} : { x: px, y: py }}
            animate={{ scale: zoomed ? 1.85 : 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            drag={zoomed}
            dragConstraints={{ left: -300, right: 300, top: -220, bottom: 220 }}
            dragElastic={0.08}
          >
            <img
              src={MASTER_IMAGE}
              alt="Master artwork of Bharat — Miniature India Park"
              data-testid="master-artwork"
              className="w-full h-full object-cover select-none"
              draggable={false}
            />

            {/* hotspots — percentage positioned, scale with the artwork */}
            {REGIONS.map((r) => (
              <button
                key={r.id}
                data-testid={`hotspot-${r.id}`}
                data-active={active === r.id}
                data-empty={r.images.length === 0}
                aria-label={`${r.name} — ${r.zone} Bharat${r.images.length === 0 ? " (artwork forthcoming)" : ""}`}
                className="hotspot"
                style={{ left: `${r.x}%`, top: `${r.y}%`, width: `${r.w}%`, height: `${r.h}%` }}
                onMouseEnter={() => setActive(r.id)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(r.id)}
                onBlur={() => setActive(null)}
                onClick={() =>
                  r.images.length > 0 ? onOpenRegion(r.id) : onAwaiting(r)
                }
              >
                <span className="hotspot-glow" />
                <span className="hotspot-dot" />
              </button>
            ))}

            {/* region name tag */}
            {activeRegion && !zoomed && (
              <motion.div
                key={activeRegion.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="region-tag absolute z-20 px-4 py-2 text-center"
                style={{
                  left: `${activeRegion.x}%`,
                  top: `${Math.max(activeRegion.y - activeRegion.h / 2 - 5, 3)}%`,
                  transform: "translateX(-50%)",
                }}
                data-testid="region-tag"
              >
                <div className="font-display text-[11px] sm:text-xs tracking-[0.3em] uppercase text-[#E8DCC4] whitespace-nowrap">
                  {activeRegion.name}
                </div>
                <div className="font-nav text-[8px] tracking-[0.3em] uppercase text-[#9A8B78] mt-1">
                  {activeRegion.images.length > 0 ? "Touch to enter" : "Artwork forthcoming"}
                </div>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* hint + zoom */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1.2 }}
        className="absolute left-5 sm:left-8 bottom-5 z-30 flex items-center gap-5"
      >
        <span className="font-serif-body italic text-sm text-[#9A8B78]" data-testid="atlas-hint">
          Explore Bharat — touch a region of the artwork
        </span>
        <button
          data-testid="zoom-toggle"
          onClick={() => setZoomed((z) => !z)}
          className="font-nav text-[9px] tracking-[0.3em] uppercase text-[#857565] border border-[rgba(197,160,89,0.2)] px-3 py-2 hover:text-[#C5A059] hover:border-[rgba(197,160,89,0.45)] transition-colors duration-500"
        >
          {zoomed ? "Whole View" : "Zoom"}
        </button>
      </motion.div>

      {/* slow editorial marquee */}
      <div className="relative z-20 border-t border-[rgba(197,160,89,0.12)] py-[9px] overflow-hidden">
        <div className="marquee-track font-nav text-[9px] tracking-[0.42em] uppercase text-[#6f6252]">
          <span className="pr-8">{MARQUEE_ITEMS.repeat(4)}</span>
          <span className="pr-8">{MARQUEE_ITEMS.repeat(4)}</span>
        </div>
      </div>
    </div>
  );
}
